import { useState } from 'react';
import { Badge } from '@savvy-kit/components/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { BellIcon } from 'lucide-react';

import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';
import { assert } from '@savvy/utils/assert';

import { useInboxAlertTypes } from '../../../features/notifications-v2/hooks/use-inbox-alert-types';
import { useNotificationsAdvisorFilter } from '../../../features/notifications-v2/use-notifications-advisor-filter';
import { notificationsMenuButtonQuery } from '../nav/advisor-side-nav/notifications-menu-button';
import NotificationsPeekPanel from './notifications-peek-panel';
import { notificationsVisitedQuery } from './notifications-visited';

function NotificationsBellButton() {
  const [open, setOpen] = useState(false);

  const { user } = useAuth();
  assert(!isNil(user) && !isNil(user.id), 'User and ID should be defined');
  const userId: string = user.id;
  const inboxAlertTypes = useInboxAlertTypes(userId);

  // Bell has no filter UI, so the effective scope always comes from the
  // resolver default — [userId] for primary advisors, [] for group-viewer
  // admins (so the backend falls back to the whole accessible group).
  const { ready: advisorFilterReady, effectivePrimaryAdvisorIds } = useNotificationsAdvisorFilter(userId, null);

  const { data: visitedData } = useQueryWithErrorLogging(notificationsVisitedQuery, {
    variables: { id: userId },
  });
  const visitedAt = visitedData?.advisor?.visitedActionCenterAt ?? null;

  const { data } = useQueryWithErrorLogging(notificationsMenuButtonQuery, {
    variables: {
      userId,
      alertTypes: [...inboxAlertTypes],
      primaryAdvisorIds: [...effectivePrimaryAdvisorIds],
      groupBulk: true,
    },
    skip: !advisorFilterReady,
  });

  const openCount = data?.alerts?.totalCount ?? 0;
  const hasCount = openCount > 0;
  // Cap the rendered count so a 3+ digit badge can't overflow the icon —
  // matches the GitHub/Slack pattern. The aria label still gets the real
  // number so screen readers report it precisely.
  const displayCount = openCount > 99 ? '99+' : openCount;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label={`Notifications${hasCount ? ` (${openCount} items need action)` : ''}`}
            className="relative flex size-9 cursor-pointer items-center justify-center rounded-md text-foreground transition-colors hover:bg-[var(--component-hover)]"
          >
            <BellIcon className="size-4" />
            <Badge
              className={cn(
                'absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] leading-none pointer-events-none',
                !hasCount && 'hidden',
              )}
              type="circle"
            >
              {displayCount}
            </Badge>
          </button>
        }
      />
      <PopoverContent align="end" sideOffset={8} className="w-auto p-0">
        {open && advisorFilterReady && (
          <NotificationsPeekPanel
            alertTypes={inboxAlertTypes}
            primaryAdvisorIds={effectivePrimaryAdvisorIds}
            visitedAt={visitedAt}
            onClose={() => setOpen(false)}
          />
        )}
      </PopoverContent>
    </Popover>
  );
}

export default NotificationsBellButton;
